import isEqual from 'lodash.isequal';
import React from 'react';
import { useFullScreenHandle } from 'react-full-screen';
import { useTranslation } from 'react-i18next';
import { getTranslation } from '../../../../constants/systemValuesTranslation';
import { getAliasForDataField } from '../../../api/alias/utils';
import { useFilterValues } from '../../../api/data/hooks';
import {
  CalendarYearYearlyTimeSelectionInput,
  CohortMetricId,
  FinancialYearYearlyTimeSelectionInput,
} from '../../../api/types-graphql';
import { OverTimeUnsegmentedViz } from '../../../common/components/data-viz-containers/over-time-unsegmented/OverTimeUnsegmentedViz';
import {
  OverTimeUnsegmentedVizConfig,
  OverTimeUnsegmentedVizExternalProps,
  OverTimeUnsegmentedVizViews,
} from '../../../common/components/data-viz-containers/over-time-unsegmented/types';
import { OverTimeSegmentedViz } from '../../../common/components/data-viz-containers/overtime-segmented-query/OverTimeSegmentedViz';
import {
  OverTimeSegmentedVizConfig,
  OverTimeSegmentedVizExternalProps,
  OverTimeSegmentedVizViews,
} from '../../../common/components/data-viz-containers/overtime-segmented-query/types';
import { ChartDataFetchError } from '../../../common/components/errors/components/ChartDataFetchError';
import { Languages } from '../../../constants';
import {
  useAliasesContext,
  useDisplayGlobalContext,
  useFilterSectionsContext,
  useGlobalLocaleContext,
  useLatestPreferencesContext,
} from '../../../context/contexts';
import {
  DataFieldWithDataType,
  OverTimeTimeSelection,
  TimeSelectionType,
} from '../../../types';

export const DEFAULT_SIMULATION_YEAR_RANGE: CalendarYearYearlyTimeSelectionInput =
  {
    start: 2025,
    end: 2034,
  };

/**
 * The HC simulation charts plot yearly buckets. Domains whose simulation data is
 * keyed by financial year (non-January FY start) must pass
 * `TimeSelectionType.FinancialYearYearly` so the yearly buckets line up with the
 * data, otherwise recent years come back empty.
 */
export type SimulationTimeSelectionType =
  | TimeSelectionType.CalendarYearYearly
  | TimeSelectionType.FinancialYearYearly;

export const DEFAULT_SIMULATION_TIME_SELECTION_TYPE: SimulationTimeSelectionType =
  TimeSelectionType.CalendarYearYearly;

export type SimulationYearRange =
  | CalendarYearYearlyTimeSelectionInput
  | FinancialYearYearlyTimeSelectionInput;

const buildSimulationTimeSelection = (
  type: SimulationTimeSelectionType,
  input: SimulationYearRange
): OverTimeTimeSelection =>
  type === TimeSelectionType.FinancialYearYearly
    ? { type: TimeSelectionType.FinancialYearYearly, input }
    : { type: TimeSelectionType.CalendarYearYearly, input };

export const DEFAULT_SIMULATION_METRICS = [
  CohortMetricId.Pm0417_2SimulatedHeadcountCount,
  CohortMetricId.Pm0467_1SimulatedLaborCostCount,
] as const;

export type SimulationMetricId = (typeof DEFAULT_SIMULATION_METRICS)[number];

export type SimulationChartProps = {
  segmentByDataField: DataFieldWithDataType | null;
  metricId: SimulationMetricId;
  simulationYearRange?: SimulationYearRange;
  simulationTimeSelectionType?: SimulationTimeSelectionType;
};

export const SimulationChart = ({
  segmentByDataField,
  metricId,
  simulationYearRange = DEFAULT_SIMULATION_YEAR_RANGE,
  simulationTimeSelectionType = DEFAULT_SIMULATION_TIME_SELECTION_TYPE,
}: SimulationChartProps) => {
  if (segmentByDataField === null) {
    return (
      <SimulationChartTotal
        metricId={metricId}
        simulationYearRange={simulationYearRange}
        simulationTimeSelectionType={simulationTimeSelectionType}
      />
    );
  }
  return (
    <SimulationChartSegmented
      segmentByDataField={segmentByDataField}
      metricId={metricId}
      simulationYearRange={simulationYearRange}
      simulationTimeSelectionType={simulationTimeSelectionType}
    />
  );
};

type SimulationChartSegmentedProps = {
  segmentByDataField: DataFieldWithDataType;
  metricId: SimulationMetricId;
  simulationYearRange: SimulationYearRange;
  simulationTimeSelectionType: SimulationTimeSelectionType;
};

const SimulationChartSegmented = ({
  segmentByDataField,
  metricId,
  simulationYearRange,
  simulationTimeSelectionType,
}: SimulationChartSegmentedProps) => {
  const filterSections = useFilterSectionsContext();
  const filterSection = filterSections.find((section) =>
    isEqual(section.dataField, segmentByDataField)
  );
  const filterDataManager = filterSection?.filterDataManagerForFilterTray;
  if (!filterDataManager) {
    return (
      <ChartDataFetchError
        errorData={{
          error: new Error(
            `Filter data manager not found for field ${segmentByDataField.dataField}`
          ),
        }}
      />
    );
  }
  const { data: filterValues } = useFilterValues(filterDataManager);
  const { t } = useTranslation();
  const aliases = useAliasesContext();

  const enFieldName =
    getAliasForDataField(aliases, Languages.EN)(segmentByDataField) ??
    getTranslation(segmentByDataField);
  const jaFieldName =
    getAliasForDataField(aliases, Languages.JA)(segmentByDataField) ??
    getTranslation(segmentByDataField);
  const enTitle = t('common:charts.dynamicSegmentedChartTitle', {
    fieldName: enFieldName,
    lng: Languages.EN,
  });
  const jaTitle = t('common:charts.dynamicSegmentedChartTitle', {
    fieldName: jaFieldName,
    lng: Languages.JA,
  });
  const dataConfig: OverTimeSegmentedVizConfig = {
    title: {
      [Languages.EN]: enTitle,
      [Languages.JA]: jaTitle,
    },
    query: {
      metric: {
        type: 'CohortMetricId',
        value: metricId,
      },
    },
    timeSelection: buildSimulationTimeSelection(
      simulationTimeSelectionType,
      simulationYearRange
    ),
    segmentBy: {
      type: 'employee-cohort',
      dataField: segmentByDataField,
      filters: filterValues?.map((filter) => {
        return filter.values[0] as string;
      }),
    },
    showTotal: true,
  };
  const preferences = useLatestPreferencesContext();
  const displayGlobalHandle = useDisplayGlobalContext();
  const [displayGlobalState] = displayGlobalHandle;
  const fullScreenHandle = useFullScreenHandle();
  const locale = useGlobalLocaleContext();
  // I should have defaults for external props so these don't need
  // to be provided each time and only the minimal thing needed for the chart
  // to work has to be provided
  const externalProps: OverTimeSegmentedVizExternalProps = {
    manualDataRefetchCountHandle: { current: 0, increment: () => {} },
    timeSliderState: null,
    filtersState: { items: [], resetActive: false },
    segmentationState: { items: [], resetActive: false },
    preferences,
    displayGlobalState,
    fullScreenHandle,
    locale,
  };
  const selectedView = OverTimeSegmentedVizViews.BAR_CHART;
  const persistenceKey = `simulation-chart-${metricId}`;
  return (
    <OverTimeSegmentedViz
      dataConfig={dataConfig}
      selectedView={selectedView}
      externalProps={externalProps}
      persistenceKey={persistenceKey}
    />
  );
};

type SimulationChartTotalProps = {
  metricId: SimulationMetricId;
  simulationYearRange: SimulationYearRange;
  simulationTimeSelectionType: SimulationTimeSelectionType;
};

const SimulationChartTotal = ({
  metricId,
  simulationYearRange,
  simulationTimeSelectionType,
}: SimulationChartTotalProps) => {
  const { t } = useTranslation();

  const enTitle = t('common:commonValues.misc.total', {
    lng: Languages.EN,
  });
  const jaTitle = t('common:commonValues.misc.total', {
    lng: Languages.JA,
  });

  const dataConfig: OverTimeUnsegmentedVizConfig = {
    title: {
      [Languages.EN]: enTitle,
      [Languages.JA]: jaTitle,
    },
    timeSelection: buildSimulationTimeSelection(
      simulationTimeSelectionType,
      simulationYearRange
    ),
    queries: [
      {
        metric: {
          type: 'CohortMetricId',
          value: metricId,
        },
        filters: `COHORT.COHORT_VALUE = '{"": ""}'`,
      },
    ],
  };

  const preferences = useLatestPreferencesContext();
  const displayGlobalHandle = useDisplayGlobalContext();
  const [displayGlobalState] = displayGlobalHandle;
  const fullScreenHandle = useFullScreenHandle();
  const locale = useGlobalLocaleContext();

  const externalProps: OverTimeUnsegmentedVizExternalProps = {
    manualDataRefetchCountHandle: { current: 0, increment: () => {} },
    timeSliderState: null,
    filtersState: { items: [], resetActive: false },
    segmentationState: { items: [], resetActive: false },
    preferences,
    displayGlobalState,
    fullScreenHandle,
    locale,
  };

  const selectedView = OverTimeUnsegmentedVizViews.BAR_CHART;
  const persistenceKey = `simulation-chart-total-${metricId}`;

  return (
    <OverTimeUnsegmentedViz
      dataConfig={dataConfig}
      selectedView={selectedView}
      externalProps={externalProps}
      persistenceKey={persistenceKey}
    />
  );
};
